// DESCRIPTION: Pure slot picker component for booking flows.
// Renders a grid of time slot pills with optional capacity display.

import React from 'react';
import { __, sprintf } from '@wordpress/i18n';
import { formatTime } from '../calendar/utils';

/**
 * SlotPicker — a pure, reusable grid of selectable time slots.
 *
 * @param {Object}   props
 * @param {Array}    props.slots                Array of slot objects: { start, end, available, spots_remaining, total_capacity, booking_count }.
 * @param {Object}   props.selectedSlot         Currently selected slot object, or null.
 * @param {Function} props.onSlotSelect         Called with the slot object when user picks a slot.
 * @param {boolean}  [props.showCapacity=false] Whether to show remaining spots (for group events).
 * @param {boolean}  [props.loading=false]      Show loading state.
 * @param {string}   [props.className='']       Additional CSS class names.
 */
const SlotPicker = ( {
	slots = [],
	selectedSlot = null,
	onSlotSelect,
	showCapacity = false,
	loading = false,
	className = '',
} ) => {
	if ( loading ) {
		return (
			<div
				className={ `nb-slot-picker nb-slot-picker--loading ${ className }`.trim() }
			>
				<div className="nb-slot-picker__skeleton">
					{ Array.from( { length: 6 } ).map( ( _, i ) => (
						<div
							key={ i }
							className="nb-slot-picker__skeleton-pill"
						/>
					) ) }
				</div>
			</div>
		);
	}

	if ( ! slots.length ) {
		return (
			<div
				className={ `nb-slot-picker nb-slot-picker--empty ${ className }`.trim() }
			>
				<p className="nb-slot-picker__empty-text">
					{ __(
						'No available time slots for this date.',
						'nemtly-booking'
					) }
				</p>
			</div>
		);
	}

	return (
		<div
			className={ `nb-slot-picker ${ className }`.trim() }
			role="listbox"
			aria-label={ __( 'Available time slots', 'nemtly-booking' ) }
		>
			<div className="nb-slot-picker__grid">
				{ slots.map( ( slot ) => {
					const isSelected =
						selectedSlot &&
						selectedSlot.start === slot.start &&
						selectedSlot.end === slot.end;
					const isUnavailable = ! slot.available;

					const pillClasses = [
						'nb-slot-picker__pill',
						isSelected ? 'nb-slot-picker__pill--selected' : '',
						isUnavailable
							? 'nb-slot-picker__pill--unavailable'
							: '',
					]
						.filter( Boolean )
						.join( ' ' );

					return (
						<button
							key={ `${ slot.start }-${ slot.end }` }
							type="button"
							className={ pillClasses }
							onClick={ () =>
								! isUnavailable && onSlotSelect( slot )
							}
							disabled={ isUnavailable }
							role="option"
							aria-selected={ isSelected }
							aria-disabled={ isUnavailable }
						>
							<span className="nb-slot-picker__time">
								{ formatTime( slot.start ) }
							</span>

							{ showCapacity && slot.available && (
								<span className="nb-slot-picker__capacity">
									{ slot.spots_remaining === 999
										? __( 'Open', 'nemtly-booking' )
										: /* translators: %d: number of spots remaining */
										  sprintf(
												__(
													'%d left',
													'nemtly-booking'
												),
												slot.spots_remaining
										  ) }
								</span>
							) }

							{ isUnavailable && (
								<span className="nb-slot-picker__full">
									{ __( 'Full', 'nemtly-booking' ) }
								</span>
							) }
						</button>
					);
				} ) }
			</div>
		</div>
	);
};

export default SlotPicker;
